import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Grid,
    Chip,
    Divider,
    Button,
    alpha,
    Stack,
    Card,
    CardContent,
    CardMedia,
    Zoom,
    Fade,
    Tooltip,
    IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExploreIcon from '@mui/icons-material/Explore';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SendIcon from '@mui/icons-material/Send';
import API from '../api/axios';
import { getImageUrl, FALLBACK_IMAGE } from '../utils/imageUtils';
import BookingForm from '../components/BookingForm';
import Testimonials from '../components/Testimonials';
import ExclusiveOffers from '../components/ExclusiveOffers';
import ExploreThemes from '../components/ExploreThemes';
import InquiryPopup from '../components/InquiryPopup';
import NewsletterSubscribe from '../components/NewsletterSubscribe';

// ============ Styled Components ============

const TourCard = styled(Paper)(({ theme }) => ({
    borderRadius: 16,
    overflow: 'hidden',
    border: '2px solid',
    borderColor: alpha('#667eea', 0.3),
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.12)',
    position: 'relative',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
}));

const SelectedBadge = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    backgroundColor: alpha('#10b981', 0.1),
    color: '#10b981',
    padding: '4px 12px',
    borderRadius: 50,
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
});

const StickyWrapper = styled(Box)({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
});

const FloatingWhatsApp = styled(Box)({
    position: 'fixed',
    bottom: 24,
    right: 24,
    zIndex: 100,
});

const SuccessContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8f9fc 0%, #eef1f8 100%)',
    py: { xs: 4, md: 6 },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: { xs: 2, sm: 4 },
}));

const SectionTitle = styled(Typography)({
    fontWeight: 700,
    fontSize: '1.15rem',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
});

const PopularTourCard = ({ tour, onClick }) => (
    <Card
        sx={{
            borderRadius: 3,
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
            },
        }}
        onClick={() => onClick(tour._id)}
    >
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9' }}>
            <CardMedia
                component="img"
                image={getImageUrl(tour.imageUrl) || FALLBACK_IMAGE}
                alt={tour.title}
                sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                }}
            />
        </Box>
        <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Box>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                    {tour.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: 14, color: '#667eea' }} />
                    <Typography variant="caption" color="text.secondary">
                        {tour.location || 'Explore'}
                    </Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const BookingPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tourId = searchParams.get('tourId');

    const [selectedTour, setSelectedTour] = useState(null);
    const [tourLoading, setTourLoading] = useState(!!tourId);
    const [tourError, setTourError] = useState('');
    const [bookingSuccess, setBookingSuccess] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [popularTours, setPopularTours] = useState([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [inquiryOpen, setInquiryOpen] = useState(false);

    useEffect(() => {
        if (tourId) {
            fetchTourDetails(tourId);
        }
        fetchPopularTours();
    }, [tourId]);

    useEffect(() => {
        if (bookingSuccess) {
            setShowSuccess(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [bookingSuccess]);

    const fetchTourDetails = async (id) => {
        try {
            setTourLoading(true);
            setTourError('');
            const { data } = await API.get(`/tours/${id}`);
            setSelectedTour(data);
        } catch (err) {
            setTourError('Failed to load tour details. Please try again.');
            console.error('Error fetching tour:', err);
        } finally {
            setTourLoading(false);
        }
    };

    const fetchPopularTours = async () => {
        try {
            const { data } = await API.get('/tours?limit=4');
            const list = data.tours || (Array.isArray(data) ? data : []);
            const top = list.slice(0, 4);
            setPopularTours(top);
        } catch (err) {
            console.error('Error fetching popular tours:', err);
        }
    };

    const handleBookingSuccess = (data) => {
        setSuccessData(data);
        setBookingSuccess(true);
    };

    const navigateToTour = (id) => {
        navigate(`/booking?tourId=${id}`);
    };

    // ============ SUCCESS SCREEN ============
    if (bookingSuccess && successData) {
        return (
            <SuccessContainer>
                <Container maxWidth="sm" disableGutters>
                    <Fade in={showSuccess} timeout={600}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 3, sm: 5 },
                                textAlign: 'center',
                                borderRadius: 4,
                                border: '1px solid rgba(0,0,0,0.06)',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Decorative gradient top */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    background: 'linear-gradient(90deg, #10b981, #34d399, #10b981)',
                                    backgroundSize: '200% 100%',
                                    animation: 'gradientShift 3s ease infinite',
                                }}
                            />

                            <Zoom in={showSuccess} timeout={500}>
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 3,
                                        boxShadow: '0 12px 40px rgba(16,185,129,0.3)',
                                    }}
                                >
                                    <CheckCircleOutlineIcon sx={{ fontSize: 44, color: 'white' }} />
                                </Box>
                            </Zoom>

                            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
                                Booking Confirmed! 🎉
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontSize: '1.05rem' }}>
                                Thank you for your booking, <strong>{successData.userName}</strong>!
                            </Typography>
                            {selectedTour && (
                                <Chip
                                    icon={<LocationOnIcon />}
                                    label={selectedTour.title}
                                    size="small"
                                    sx={{
                                        mt: 1,
                                        mb: 2,
                                        bgcolor: alpha('#667eea', 0.08),
                                        color: 'primary.main',
                                        fontWeight: 600,
                                        borderRadius: 50,
                                        px: 1,
                                    }}
                                />
                            )}

                            <Divider sx={{ my: 3 }} />

                            <Box sx={{ textAlign: 'left', bgcolor: '#f8f9fc', borderRadius: 2, p: 2.5, mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                                    Booking Summary
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">Service:</Typography>
                                        <Typography variant="body2" fontWeight={600}>{successData.serviceType || successData.service_type || 'Standard Booking'}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">Date:</Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {successData.bookingDate ? new Date(successData.bookingDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">People:</Typography>
                                        <Typography variant="body2" fontWeight={600}>{successData.numberOfPeople}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="text.secondary">Meal:</Typography>
                                        <Typography variant="body2" fontWeight={600}>{successData.mealPreference}</Typography>
                                    </Box>
                                    {successData.preferredContactTime && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Contact Time:</Typography>
                                            <Typography variant="body2" fontWeight={600}>{successData.preferredContactTime}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    bgcolor: alpha('#667eea', 0.04),
                                    borderRadius: 2,
                                    p: 2,
                                    mb: 3,
                                    textAlign: 'left',
                                    border: `1px solid ${alpha('#667eea', 0.1)}`,
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#667eea', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <SendIcon sx={{ fontSize: 16 }} />
                                    What Happens Next?
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        ✅ A confirmation email has been sent to <strong>{successData.email}</strong>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        ✅ Our team will contact you within 24 hours to confirm details
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        ✅ You will receive a WhatsApp confirmation shortly
                                    </Typography>
                                </Box>
                            </Box>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/')}
                                    sx={{
                                        borderRadius: 2.5,
                                        px: 4,
                                        py: 1.2,
                                        fontWeight: 700,
                    background: 'linear-gradient(135deg, #0C2340 0%, #183B63 100%)',
                    boxShadow: '0 8px 25px rgba(12,35,64,0.3)',
                    '&:hover': { boxShadow: '0 12px 35px rgba(12,35,64,0.4)', transform: 'translateY(-1px)' },
                                    }}
                                >
                                    Back to Home
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/tours')}
                                    startIcon={<ExploreIcon />}
                                    sx={{
                                        borderRadius: 2.5,
                                        px: 4,
                                        py: 1.2,
                                        fontWeight: 700,
                    borderWidth: 2,
                    borderColor: '#0C2340',
                    color: '#0C2340',
                    '&:hover': { borderWidth: 2, borderColor: '#0C2340', bgcolor: alpha('#0C2340', 0.04) },
                                    }}
                                >
                                    Browse More Tours
                                </Button>
                            </Stack>
                        </Paper>
                    </Fade>
                </Container>
                <FloatingWhatsApp>
                    <Tooltip title="Need help? Chat with us" arrow placement="left">
                        <IconButton
                            sx={{
                                width: 56,
                                height: 56,
                                bgcolor: '#25D366',
                                color: 'white',
                                boxShadow: '0 8px 24px rgba(37,211,102,0.4)',
                                '&:hover': {
                                    bgcolor: '#1ebe5d',
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.3s ease',
                            }}
                            onClick={() => window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER?.replace(/\+/g, '') || '919876543210'}`, '_blank')}
                        >
                            <WhatsAppIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                    </Tooltip>
                </FloatingWhatsApp>
            </SuccessContainer>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f8f9fc 0%, #eef1f8 100%)',
                py: { xs: 3, md: 6 },
            }}
        >
            <Container maxWidth="lg">            

                {/* ============ LOADING STATE ============ */}
                {tourLoading && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            mb: 4,
                            p: 4,
                            bgcolor: 'white',
                            borderRadius: 4,
                            border: '1px solid rgba(0,0,0,0.06)',
                        }}
                    >
                        <CircularProgress size={32} sx={{ color: '#667eea' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            Loading tour details...
                        </Typography>
                    </Box>
                )}

                {/* ============ ERROR STATE ============ */}
                {tourError && (
                    <Alert severity="warning" sx={{ mb: 4, borderRadius: 2 }} onClose={() => setTourError('')}>
                        {tourError}
                    </Alert>
                )}

                {/* ============ NO TOUR SELECTED — FALLBACK ============ */}
                {!tourId && !tourLoading && !selectedTour && (
                    <Fade in={true} timeout={400}>
                        <Box sx={{ mb: 5 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 3, md: 5 },
                                    textAlign: 'center',
                                    borderRadius: 4,
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                                    mb: 4,
                                }}
                            >
                                <ExploreIcon sx={{ fontSize: 56, color: alpha('#667eea', 0.3), mb: 2 }} />
                                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                                    Start Your Journey
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                                    Select a tour package to view details and book your dream vacation. 
                                    Our expert-curated packages offer the best travel experiences.
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/tours')}
                                    sx={{
                                        borderRadius: 2.5,
                                        px: 4,
                                        py: 1.2,
                                        fontWeight: 700,
                    background: 'linear-gradient(135deg, #0C2340 0%, #183B63 100%)',
                    boxShadow: '0 8px 25px rgba(12,35,64,0.3)',
                                    }}
                                >
                                    Browse All Tours
                                </Button>
                            </Paper>

                            {/* Popular Tours Grid as fallback */}
                            {popularTours.length > 0 && (
                                <Box sx={{ mb: 4 }}>
                                    <SectionTitle sx={{ mb: 2.5 }}>
                                        <LocalOfferIcon sx={{ color: '#667eea', fontSize: 20 }} />
                                        Popular Tour Packages
                                    </SectionTitle>
                                    <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                                        {popularTours.map((tour) => (
                                            <Grid item xs={6} sm={3} key={tour._id}>
                                                <PopularTourCard tour={tour} onClick={navigateToTour} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            <Testimonials variant="compact" />
                        </Box>
                    </Fade>
                )}

                {/* ============ TWO-COLUMN LAYOUT (tour selected) ============ */}
                {(tourId || selectedTour) && !tourLoading && (
                    <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch" sx={{ height: '100%', minHeight: { md: 620 } }}>
                        {/* LEFT COLUMN: Tour Details + Trust (Removed Support Banner) */}
                        <Grid item xs={12} md={5} lg={4} sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
                            <StickyWrapper sx={{ flex: 1 }}>
                                {selectedTour && (
                                    <TourCard sx={{ flex: 1, height: '100%' }}>
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                            height: { xs: 180, md: 200 },
                                            flexShrink: 0,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={getImageUrl(selectedTour.imageUrl) || FALLBACK_IMAGE}
                                                alt={selectedTour.title}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = FALLBACK_IMAGE;
                                                }}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    transition: 'transform 0.6s ease',
                                                    '&:hover': {
                                                        transform: 'scale(1.05)',
                                                    },
                                                }}
                                            />
                                        </Box>

                                        <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                                                <SelectedBadge>
                                                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                                                    Selected Tour
                                                </SelectedBadge>
                                                {selectedTour.category && (
                                                    <Chip
                                                        label={selectedTour.category}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem',
                                                            bgcolor: alpha('#667eea', 0.08),
                                                            color: '#667eea',
                                                            borderRadius: 50,
                                                            height: 24,
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 800,
                                                    mb: 1.5,
                                                    color: 'text.primary',
                                                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {selectedTour.title}
                                            </Typography>

                                            {selectedTour.description && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mb: 2,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        lineHeight: 1.6,
                                                    }}
                                                >
                                                    {selectedTour.description}
                                                </Typography>
                                            )}

                                            <Divider sx={{ my: 2 }} />

                                            <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 3 }, flexWrap: 'wrap', mb: 2 }}>
                                            </Box>

                                            <Box sx={{ flexGrow: 1 }} />

                                            {selectedTour.highlights && selectedTour.highlights.length > 0 && (
                                                <Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: 0.5,
                                                            color: 'text.secondary',
                                                            mb: 1,
                                                            display: 'block',
                                                            fontSize: '0.65rem',
                                                        }}
                                                    >
                                                        Highlights
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                                        {selectedTour.highlights.slice(0, 5).map((h, i) => (
                                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CheckCircleIcon sx={{ fontSize: 15, color: '#10b981', flexShrink: 0 }} />
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                                    {h}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                        {selectedTour.highlights.length > 5 && (
                                                            <Typography variant="caption" color="#667eea" sx={{ fontWeight: 600, mt: 0.5 }}>
                                                                +{selectedTour.highlights.length - 5} more highlights
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            )}

                                            <Box
                                                sx={{
                                                    mt: 2.5,
                                                    pt: 2,
                                                    borderTop: `1px solid ${alpha('#000', 0.06)}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    justifyContent: 'center',
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <SupportAgentIcon sx={{ fontSize: 16, color: '#667eea' }} />
                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, textAlign: 'center' }}>
                                                    Need help? Call us at <strong>{process.env.REACT_APP_PHONE_NUMBER || '+91 9876543210'}</strong>
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TourCard>
                                )}

                            </StickyWrapper>
                        </Grid>

                        {/* RIGHT COLUMN: Booking Form + Support Banner (Moved Here) */}
                        <Grid item xs={12} md={7} lg={8} sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
                            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', width: '100%', flex: 1 }}>
                                <BookingForm
                                    tourId={tourId}
                                    selectedTour={selectedTour}
                                    onSuccess={handleBookingSuccess}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                )}

                {/* ============ POST-FORM SECTIONS (shown when tour is selected) ============ */}
                {(tourId || selectedTour) && !tourLoading && (
                    <>
                        <Box sx={{ mt: { xs: 3, md: 5 } }}>
                            <Divider sx={{ mb: 3 }} />
                            <Box sx={{ mx: { xs: 0, sm: 0 } }}>
                                <Testimonials variant="compact" />
                            </Box>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <ExclusiveOffers />
                        </Box>
                    </>
                )}

                {/* ============ EXPLORE THEMES (always shown) ============ */}
                <ExploreThemes />

                {/* ============ NEWSLETTER (always shown) ============ */}
                <NewsletterSubscribe />

            </Container>

            {/* Floating WhatsApp Button */}
            <FloatingWhatsApp>
                <Tooltip title="Chat with us on WhatsApp" arrow placement="left">
                    <IconButton
                        sx={{
                            width: 52,
                            height: 52,
                            bgcolor: '#25D366',
                            color: 'white',
                            boxShadow: '0 6px 20px rgba(37,211,102,0.35)',
                            '&:hover': {
                                bgcolor: '#1ebe5d',
                                transform: 'scale(1.1)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                        onClick={() => window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER?.replace(/\+/g, '') || '919876543210'}`, '_blank')}
                    >
                        <WhatsAppIcon sx={{ fontSize: 26 }} />
                    </IconButton>
                </Tooltip>
            </FloatingWhatsApp>

            <InquiryPopup open={inquiryOpen} onClose={() => setInquiryOpen(false)} />
        </Box>
    );
};

export default BookingPage;